import { useState, type FormEvent } from 'react';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { editLesson } from '../../../Redux/lessonsSlice';
import { selectActiveStudents } from '../../../Redux/selectors';
import { useTranslation } from '../../../i18n/useTranslation';
import { useDisplayName } from '../../../Utils/useDisplayName';
import { ApiError } from '../../../Models/ApiModel';
import type { LessonModel } from '../../../Models/LessonModel';
import type { LessonPatch } from '../../../Services/LessonService';
import { Modal } from '../../SharedArea/Modal/Modal';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import './QuickAddLesson.css';

/**
 * Editing a lesson that already exists: who, when, and how much.
 *
 * The status is deliberately not here. It has its own sheet, reached from the
 * same row, because changing what a lesson costs and marking it paid are
 * different intentions and a single form that does both invites the wrong one.
 *
 * A student who has been archived since the lesson was taught still appears in
 * the list while they are this lesson's student, or editing the time of an old
 * lesson would silently reassign it to whoever happens to be first.
 */
export function LessonEditor({ lesson, onClose }: { lesson: LessonModel; onClose: () => void }) {
  const { tr } = useTranslation();
  const displayName = useDisplayName();
  const dispatch = useAppDispatch();
  const active = useAppSelector(selectActiveStudents);
  const options: { name: string; price: string }[] = active
    .map(candidate => ({ name: candidate.name, price: candidate.price }));
  if (!options.some(candidate => candidate.name === lesson.student)) {
    options.push({ name: lesson.student, price: lesson.amount });
  }

  const [student, setStudent] = useState(lesson.student);
  const [date, setDate] = useState(lesson.date);
  const [time, setTime] = useState(lesson.time ?? '');
  const [amount, setAmount] = useState(lesson.amount);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickStudent(name: string) {
    setStudent(name);
    // Only the price follows the student, and only when it is a real change.
    // Reopening the form on the same student must not overwrite an amount the
    // tutor set by hand.
    const picked = active.find(candidate => candidate.name === name);
    if (picked && name !== lesson.student) setAmount(picked.price);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    // Only what changed is sent. The server treats an absent field as "keep",
    // and `time: null` as "clear it", which is what an emptied field means.
    const patch: LessonPatch = {};
    if (student !== lesson.student) patch.student = student;
    if (date !== lesson.date) patch.date = date;
    if (amount !== lesson.amount) patch.amount = amount;
    if ((time || null) !== (lesson.time ?? null)) patch.time = time || null;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    try {
      await dispatch(editLesson({ id: lesson.id, patch })).unwrap();
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={tr('editLesson')}
      subtitle={displayName(lesson.student)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ghost-button" onClick={onClose}>{tr('cancel')}</button>
          <button type="submit" form="edit-lesson" className="primary-button" disabled={busy || !student || !date || !amount}>
            {busy ? <Spinner inline /> : tr('save')}
          </button>
        </>
      }
    >
      <form id="edit-lesson" className="quick-add" onSubmit={submit}>
        <label className="field is-wide">
          <span>{tr('studentName')}</span>
          <select className="text-input" value={student} onChange={e => pickStudent(e.target.value)} required>
            {options.map(candidate => (
              <option key={candidate.name} value={candidate.name}>{displayName(candidate.name)}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{tr('date')}</span>
          <input className="text-input" type="date" value={date} onChange={e => setDate(e.target.value)} required />
        </label>
        <label className="field">
          <span>{tr('time')}</span>
          <input className="text-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
        </label>
        <label className="field is-wide">
          <span>{tr('amount')}</span>
          <input
            className="text-input"
            inputMode="numeric"
            value={amount}
            onChange={e => setAmount(e.target.value.replace(/\D/g, ''))}
            required
          />
        </label>
        {error && <p className="quick-add-error" role="alert">{tr('errorGeneric')}</p>}
      </form>
    </Modal>
  );
}
